import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

export const WHATSAPP_LABEL_PRESET_VALUES = [
  "combine-frete",
  "compre-whatsapp",
  "fale-consultor",
];

/** DB: 1 (legado) e null = herdar loja; 0 = ocultar; 2 = exibir explícito */
export const ECOM_DISPLAY_INHERIT = 1;
export const ECOM_DISPLAY_HIDE = 0;
export const ECOM_DISPLAY_SHOW = 2;

const LABEL_PRESETS = [
  { value: "store", labelKey: "form.ecomWhatsappLabelStoreDefault" },
  { value: "combine-frete", labelKey: "form.ecomWhatsappLabelCombineFrete" },
  { value: "compre-whatsapp", labelKey: "form.ecomWhatsappLabelCompreWhatsapp" },
  { value: "fale-consultor", labelKey: "form.ecomWhatsappLabelFaleConsultor" },
  { value: "custom", labelKey: "form.ecomWhatsappLabelCustom" },
];

const DISPLAY_MODES = [
  { value: "store", labelKey: "form.ecomDisplayStoreDefault" },
  { value: "show", labelKey: "form.ecomDisplayShow" },
  { value: "hide", labelKey: "form.ecomDisplayHide" },
];

export function resolveWhatsappLabelMode(rotulo) {
  const raw = String(rotulo ?? "").trim();
  // combine-frete é o default legado → tratar como "padrão da loja"
  if (!raw || raw === "combine-frete") return "store";
  if (WHATSAPP_LABEL_PRESET_VALUES.includes(raw)) return raw;
  return "custom";
}

export function resolveEcomDisplayMode(value) {
  if (value == null || value === "" || value === "store") return "store";
  const n = Number(value);
  if (n === ECOM_DISPLAY_HIDE) return "hide";
  if (n === ECOM_DISPLAY_SHOW) return "show";
  // 1 e demais → herdar (compatível com DEFAULT 1 atual no banco)
  return "store";
}

export function ecomDisplayModeToDb(mode) {
  if (mode === "hide") return ECOM_DISPLAY_HIDE;
  if (mode === "show") return ECOM_DISPLAY_SHOW;
  return ECOM_DISPLAY_INHERIT; // store → 1 (sem exigir NULL no banco)
}

export default function EcomPageSection({ formData, handleText, setFormData }) {
  const { t } = useTranslation("products");
  const whatsappMode = resolveEcomDisplayMode(formData.ecom_whatsapp_frete);
  const benefitsMode = resolveEcomDisplayMode(formData.ecom_exibir_beneficios);
  const labelMode =
    formData.ecom_whatsapp_label_mode ||
    resolveWhatsappLabelMode(formData.ecom_whatsapp_frete_rotulo);
  const showCustomField = labelMode === "custom" && whatsappMode === "show";
  const labelEnabled = whatsappMode === "show";

  const handleDisplayChange = (field) => (e) => {
    const mode = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: ecomDisplayModeToDb(mode),
    }));
  };

  const handleLabelModeChange = (e) => {
    const mode = e.target.value;
    setFormData((prev) => {
      const prevRotulo = String(prev.ecom_whatsapp_frete_rotulo ?? "").trim();
      const wasPreset = WHATSAPP_LABEL_PRESET_VALUES.includes(prevRotulo);

      if (mode === "store") {
        return {
          ...prev,
          ecom_whatsapp_label_mode: "store",
          // 1 legado / combine-frete = herdar; grava o default legado para não exigir NULL
          ecom_whatsapp_frete_rotulo: "combine-frete",
        };
      }

      if (mode === "custom") {
        return {
          ...prev,
          ecom_whatsapp_label_mode: "custom",
          ecom_whatsapp_frete_rotulo: wasPreset || prevRotulo === "combine-frete" ? "" : prevRotulo,
        };
      }

      return {
        ...prev,
        ecom_whatsapp_label_mode: mode,
        ecom_whatsapp_frete_rotulo: mode,
      };
    });
  };

  return (
    <Card variant="outlined" sx={{ mb: 6 }}>
      <CardHeader
        title={t("form.ecomPageTitle")}
        subheader={t("form.ecomPageSubtitle")}
        sx={{ pb: 0.5, "& .MuiCardHeader-title": { fontSize: 16, fontWeight: 600 } }}
      />
      <CardContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              size="small"
              label={t("form.ecomWhatsappFreteEnabled")}
              value={whatsappMode}
              onChange={handleDisplayChange("ecom_whatsapp_frete")}
              helperText={t("form.ecomWhatsappFreteHint")}
            >
              {DISPLAY_MODES.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {t(p.labelKey)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              size="small"
              label={t("form.ecomBenefitsGridEnabled")}
              value={benefitsMode}
              onChange={handleDisplayChange("ecom_exibir_beneficios")}
              helperText={t("form.ecomDisplayStoreHint")}
            >
              {DISPLAY_MODES.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {t(p.labelKey)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              size="small"
              label={t("form.ecomWhatsappLabel")}
              value={labelMode}
              onChange={handleLabelModeChange}
              disabled={!labelEnabled}
            >
              {LABEL_PRESETS.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {t(p.labelKey)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {showCustomField ? (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label={t("form.ecomWhatsappCustomLabel")}
                name="ecom_whatsapp_frete_rotulo"
                value={formData.ecom_whatsapp_frete_rotulo || ""}
                onChange={handleText}
                placeholder={t("form.ecomWhatsappCustomLabelPlaceholder")}
              />
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              {t("form.ecomPageInheritHint")}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
